import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, ChevronRight, Loader2, LayoutList } from 'lucide-react';
import { HcQuiz } from './HcQuiz';
import { toast } from 'sonner';
import { hcApi, hcError, HC_FALLBACK_COURSE_IMG } from './api';
import { HcLayout } from './HcLayout';
import { CourseOutline } from './HcCourseOutline';
import { ProgressBar, courseCta, LockedNote } from './HcCourseBits';
import { OrientationCelebration, ModuleUnlockCelebration } from './HcCelebration';
import { GraduationCelebration } from './HcGraduation';
import { requestCertificateCheck } from './HcCertificateBits';
import { useLessonVideoGuards, VideoLockHint, MainLessonVideo } from './HcVideoGuard';

const useLearningTimer = (courseId, active) => {
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') hcApi.post(`/courses/${courseId}/time`, { seconds: 60 }).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [courseId, active]);
};

const Overview = ({ course, onStart }) => (
  <div className="hc-fade-up" data-testid="hc-course-overview">
    <img src={course.image_url || HC_FALLBACK_COURSE_IMG} alt="" className="w-full h-56 object-cover rounded-2xl" />
    <h1 className="text-3xl font-extrabold text-[#363636] mt-6">{course.title}</h1>
    <p className="text-gray-600 mt-3 leading-relaxed">{course.description}</p>
    <div className="mt-6 max-w-md">
      <div className="flex justify-between text-sm mb-1.5"><span>{course.completed_lessons} of {course.total_lessons} lessons</span><span className="font-bold">{course.progress}%</span></div>
      <ProgressBar value={course.progress} />
    </div>
    {course.next_lesson_id && (
      <button onClick={onStart} className="mt-6 h-12 px-8 rounded-xl bg-[#922A8C] text-white font-semibold flex items-center gap-2 hover:bg-[#7a2275]" data-testid="hc-overview-continue">
        {courseCta(course)} <ArrowRight className="w-4 h-4" />
      </button>
    )}
  </div>
);

const LessonView = ({ data, onComplete, completing, go, courseId, onQuizPassed }) => {
  const { lesson } = data;
  const mainRef = useRef(null);
  const contentRef = useRef(null);
  const required = data.videos?.required || [];
  const [watched, setWatched] = useState(() => new Set(required.filter((k) => data.videos?.progress?.[k]?.completed)));
  useLessonVideoGuards({ courseId, lessonId: lesson.id, videos: data.videos, free: data.completed, mainRef, contentRef,
    onWatched: (key) => setWatched((w) => new Set([...w, key])) });
  const remaining = required.filter((k) => !watched.has(k)).length;
  const videoLocked = !data.completed && remaining > 0;
  const nextLocked = !data.completed && data.next_locked;
  return (
    <div className="hc-fade-up" key={lesson.id} data-testid="hc-lesson-view">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-[#363636]" data-testid="hc-lesson-title">{lesson.title}</h1>
      {lesson.type === 'VIDEO' && lesson.video_url && <MainLessonVideo ref={mainRef} lesson={lesson} noRate={!data.completed} />}
      {lesson.html && <div ref={contentRef} className="hc-content mt-5" data-testid="hc-lesson-content" dangerouslySetInnerHTML={{ __html: lesson.html }} />}
      {videoLocked && data.can_complete && <VideoLockHint remaining={remaining} />}
      {lesson.type === 'QUIZ' && (
        <HcQuiz key={lesson.id} courseId={courseId} lessonId={lesson.id} quiz={data.quiz} onPassed={onQuizPassed} />
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8">
        <button onClick={() => go(data.previous_lesson_id)} disabled={!data.previous_lesson_id} data-testid="hc-prev-lesson"
          className="h-12 rounded-xl border-2 border-gray-200 font-semibold text-[#363636] flex items-center justify-center gap-2 hover:border-[#922A8C] disabled:opacity-40">
          <ArrowLeft className="w-4 h-4" /> Previous Lesson
        </button>
        {data.can_complete ? (
          <button onClick={onComplete} disabled={data.completed || completing || videoLocked} data-testid="hc-mark-complete"
            title={videoLocked ? 'Watch the whole video first' : undefined}
            className={`h-12 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors disabled:cursor-not-allowed ${data.completed ? 'bg-green-50 text-green-700 border-2 border-green-200' : videoLocked ? 'bg-gray-200 text-gray-500' : 'bg-[#922A8C] text-white hover:bg-[#7a2275]'}`}>
            {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : data.completed ? <><Check className="w-4 h-4" /> Completed</> : 'Mark as Complete'}
          </button>
        ) : <div className="hidden sm:block" />}
        <button onClick={() => go(data.next_lesson_id)} disabled={!data.next_lesson_id || nextLocked} data-testid="hc-next-lesson"
          title={nextLocked ? 'Complete this step to unlock the next one' : undefined}
          className="h-12 rounded-xl border-2 border-gray-200 font-semibold text-[#363636] flex items-center justify-center gap-2 hover:border-[#922A8C] disabled:opacity-40">
          Next Lesson <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default function HcCoursePlayerPage() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState('');
  const [completing, setCompleting] = useState(false);
  const [celebrate, setCelebrate] = useState(null);
  const [unlocked, setUnlocked] = useState(null);
  const [graduated, setGraduated] = useState(false);

  const loadCourse = useCallback(() => hcApi.get(`/courses/${courseId}`).then(({ data }) => setCourse(data)).catch((e) => setError(hcError(e))), [courseId]);
  useEffect(() => { loadCourse(); }, [loadCourse]);
  useEffect(() => {
    setLesson(null);
    setError('');
    if (!lessonId) return;
    hcApi.get(`/courses/${courseId}/lessons/${lessonId}`).then(({ data }) => setLesson(data)).catch((e) => setError(hcError(e)));
    window.scrollTo({ top: 0 });
  }, [courseId, lessonId]);
  useLearningTimer(courseId, !!lessonId);

  const go = (id) => id && navigate(`/helpconnect/courses/${courseId}/lessons/${id}`);
  const quizPassed = (res) => {
    setLesson((l) => ({ ...l, completed: true }));
    if (res.orientation_completed) setCelebrate({ course: res.unlocked_course });
    else if (res.pathways_completed) setGraduated(true);
    else if (res.module_unlocked) setUnlocked(res.module_unlocked);
    else if (res.certificate_earned) requestCertificateCheck();
    loadCourse();
  };
  const complete = async () => {
    setCompleting(true);
    try {
      const { data: res } = await hcApi.post(`/courses/${courseId}/lessons/${lessonId}/complete`);
      setLesson((l) => ({ ...l, completed: true }));
      toast.success('Lesson completed');
      if (res.orientation_completed) setCelebrate({ course: res.unlocked_course });
      else if (res.pathways_completed) setGraduated(true);
      else if (res.module_unlocked) setUnlocked(res.module_unlocked);
      else if (res.certificate_earned) requestCertificateCheck();
      loadCourse();
    } catch (e) {
      toast.error(hcError(e));
    } finally {
      setCompleting(false);
    }
  };

  return (
    <HcLayout>
      {celebrate && <OrientationCelebration course={celebrate.course} onClose={() => { setCelebrate(null); requestCertificateCheck(); }} />}
      {graduated && <GraduationCelebration onClose={() => { setGraduated(false); requestCertificateCheck(); }} />}
      {unlocked && <ModuleUnlockCelebration unlock={unlocked} onClose={() => { setUnlocked(null); requestCertificateCheck(); }} />}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <nav className="flex items-center gap-2 text-sm text-gray-500" data-testid="hc-breadcrumb">
          <Link to="/helpconnect/courses" className="hover:text-[#922A8C]">My Courses</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to={`/helpconnect/courses/${courseId}`} className="hover:text-[#922A8C] font-medium text-[#363636]" data-testid="hc-breadcrumb-course">{course?.title || 'Course'}</Link>
        </nav>
        {lessonId && (
          <Link to={`/helpconnect/courses/${courseId}`} className="flex items-center gap-2 text-[#922A8C] font-semibold hover:underline" data-testid="hc-course-overview-link">
            <LayoutList className="w-4 h-4" /> Course Overview
          </Link>
        )}
      </div>
      {error && (error.includes('Orientation') || error.includes('previous step') ? (
        <div className="max-w-xl bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="hc-player-locked">
          <LockedNote message={error} id="player" />
          <Link to="/helpconnect/courses" className="inline-flex mt-4 h-11 px-6 items-center rounded-xl bg-[#922A8C] text-white font-semibold hover:bg-[#7a2275]" data-testid="hc-go-to-orientation">Go to My Courses</Link>
        </div>
      ) : <p className="text-red-600 mb-4" data-testid="hc-player-error">{error}</p>)}
      {!course && !error && <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#922A8C]" /></div>}
      {course && (
        <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start">
          <div className="order-2 lg:order-1 min-w-0"><CourseOutline course={course} activeId={lessonId} onOpen={go} /></div>
          <div className="order-1 lg:order-2 min-w-0 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-8 min-h-[400px]">
            {!lessonId && <Overview course={course} onStart={() => go(course.next_lesson_id)} />}
            {lessonId && !lesson && !error && <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#922A8C]" /></div>}
            {lessonId && lesson && <LessonView data={lesson} onComplete={complete} completing={completing} go={go} courseId={courseId} onQuizPassed={quizPassed} />}
          </div>
        </div>
      )}
    </HcLayout>
  );
}
